import React, { useState, useEffect, useRef } from "react";
import { Offcanvas, Accordion, Dropdown } from "react-bootstrap";
import Icons from "../../../utils/icons";
import Notification_Icons, { Notification_Type } from "./NotificationIconType";
// @ts-ignore
import ToggleButton from "react-toggle-button";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/Store";
import {
  getNotification,
  readNotification,
} from "../../../services/NotificaionAPI";
import { NOTIFICATIONS_PRIORITY } from "../../../common/Constant";
import SearchInput from "../../../common/InlineSearch";
import { getAppointmentDetails } from "./NotificationUtils";

interface NotificationDrawerProps {
  show: boolean;
  handleClose: () => void;
}

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  show,
  handleClose,
}) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<string>("");
  const [showRead, setShowRead] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [priorityCount, setPriorityCount] = useState<any[]>([]);
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchQuerylength, setSearchQuerylength] = useState<string>("");
  const searchInputRef = useRef<any>(null);

  const fetchNotifications = async (
    isReadValue: number,
    selectedPriority: string,
  ) => {
    const payload = {
      recipientUserId: loggedInUserid,
      priority: selectedPriority,
      isRead: isReadValue,
      searchString: searchQuery.length >= 3 ? searchQuery : "",
      triggeredById: loggedInUserid,
    };

    try {
      const response = await getNotification(payload);
      if (response?.result) {
        setNotifications(response.result);
      }
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  const fetchCountNotifications = async () => {
    const payload = {
      recipientUserId: loggedInUserid,
      priority: "",
      isRead: null,
      searchString: searchQuery,
      triggeredById: loggedInUserid,
    };

    try {
      const response = await getNotification(payload);
      if (response?.result) {
        setPriorityCount(response.result);
      }
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  const updateNotification = async (
    notificationIds: string,
    shouldRefetch = false,
    isRead: number,
  ) => {
    const payload = {
      notificationIds,
      isRead,
    };

    try {
      const response = await readNotification(payload);

      if (response?.isSuccess) {
        if (shouldRefetch) {
          fetchNotifications(showRead ? 0 : 1, selectedPriority);
        } else {
          const idList = notificationIds.split(",").map((id) => Number(id));
          setNotifications((prev) =>
            prev.map((notif) =>
              idList.includes(notif.notificationRequestRecipientId)
                ? { ...notif, isRead }
                : notif,
            ),
          );
        }
      }
    } catch (error) {
      console.error("Failed to update notification(s)", error);
    }
  };

  useEffect(() => {
    if (show && loggedInUserid) {
      fetchNotifications(showRead ? 1 : 0, ""); // ✅ Read = 1, Unread = 0
      fetchCountNotifications();
    }
  }, [show, loggedInUserid, showRead]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedQuery = searchQuery.trim();

      if (trimmedQuery.length >= 3) {
        fetchNotifications(showRead ? 1 : 0, selectedPriority);
      } else {
        // ✅ When below 3 chars, reset to full list
        fetchNotifications(showRead ? 1 : 0, selectedPriority);
      }
    }, 300); // debounce delay

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, showRead, selectedPriority, show, loggedInUserid]);

  const counts =
    priorityCount.length > 0
      ? {
          criticalCount: priorityCount[0].critical_Count,
          mediumCount: priorityCount[0].medium_Count,
          lowCount: priorityCount[0].low_Count,
        }
      : { criticalCount: 0, mediumCount: 0, lowCount: 0 };

  useEffect(() => {
    if (!show) {
      setActiveKey(null);
      setSelectedPriority("");
      setShowRead(true); // ✅ Default toggle to Read
    }
  }, [show]);

  const handlePriorityClick = (
    priorityKey: keyof typeof NOTIFICATIONS_PRIORITY,
  ) => {
    const selected = NOTIFICATIONS_PRIORITY[priorityKey];
    setSelectedPriority(selected);
    setActiveKey(null);
    fetchNotifications(showRead ? 1 : 0, selected);
  };

  const handleMarkAllAsRead = () => {
    const unreadIds = notifications
      .filter((n) => n.isRead !== 1)
      .map((n) => String(n.notificationRequestRecipientId));

    if (unreadIds.length > 0) {
      const joinedIds = unreadIds.join(",");
      updateNotification(joinedIds, true, 1);
    }
  };

  const getElapsedTime = (createdDate: string) => {
    const created = new Date(createdDate);
    const now = new Date();
    const diff = now.getTime() - created.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days} day${days > 1 ? "s" : ""} Ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} Ago`;
    return `${minutes} minute${minutes !== 1 ? "s" : ""} Ago`;
  };

  useEffect(() => {
    if (!show) {
      setActiveKey(null);
      setSelectedPriority("");
      setShowRead(false);
    }
  }, [show]);

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleReset = () => {
    searchInputRef.current?.clear();
    setSearchQuery("");
    setSearchQuerylength("");
    fetchNotifications(showRead ? 1 : 0, selectedPriority);
  };

  const handleSearchQueryChangeForlength = (query: string) => {
    setSearchQuerylength(query?.trim());
  };

  const formatDateTimeWithRange = (dateTime: string) => {
    if (!dateTime) return "N/A";
    // Extract date part (before space + [ )
    const datePart = dateTime.split(" [")[0];
    const match = dateTime.match(/\[(.*?)\]/);
    if (!match) return datePart;

    const [start, end] = match[1].split(" - ");
    if (!start || !end) return datePart;

    const to12Hour = (timeStr: string) => {
      const [h, m] = timeStr.split(":");
      return new Date(1970, 0, 1, +h, +m)
        .toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace("am", "AM")
        .replace("pm", "PM");
    };

    return `${datePart} [${to12Hour(start)} - ${to12Hour(end)}]`;
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        handleReset(); // reset search + refetch
        handleClose(); // close the offcanvas
      }}
      placement="end"
      style={{ width: "450px" }}
    >
      <Offcanvas.Header className="notification-header p-0 px-2">
        <Offcanvas.Title className="d-flex flex-row align-items-center py-1 w-100">
          <span className="text-dark ">Notifications</span>
          <div className="priority-container d-flex flex-row">
            {/* Critical */}
            <div
              className={`priority-row ${
                selectedPriority === NOTIFICATIONS_PRIORITY.Critical
                  ? "active"
                  : ""
              }`}
              onClick={() => handlePriorityClick("Critical")}
            >
              <span className="dot-bullet critical"></span>
              <span className="priority-label">Critical</span>
              <span className="priority-count critical-count">
                {counts.criticalCount}
              </span>
            </div>

            {/* Medium */}
            <div
              className={`priority-row ${
                selectedPriority === NOTIFICATIONS_PRIORITY.Medium
                  ? "active"
                  : ""
              }`}
              onClick={() => handlePriorityClick("Medium")}
            >
              <span className="dot-bullet medium"></span>
              <span className="priority-label">Medium</span>
              <span className="priority-count medium-count">
                {counts.mediumCount}
              </span>
            </div>

            {/* Low */}
            <div
              className={`priority-row ${
                selectedPriority === NOTIFICATIONS_PRIORITY.Low ? "active" : ""
              }`}
              onClick={() => handlePriorityClick("Low")}
            >
              <span className="dot-bullet low"></span>
              <span className="priority-label">Low</span>
              <span className="priority-count low-count">
                {counts.lowCount}
              </span>
            </div>
          </div>

          <div className="toggle-container ms-2">
            <ToggleButton
              value={showRead}
              onToggle={() => {
                const newShowRead = !showRead;
                setShowRead(newShowRead);
                setActiveKey(null);
                fetchNotifications(newShowRead ? 1 : 0, selectedPriority); // ✅ Read = 1, Unread = 0
                searchInputRef.current?.clear();
                setSearchQuery("");
                setSearchQuerylength("");
              }}
              activeLabel="Read"
              inactiveLabel="Unread"
              colors={{
                activeThumb: { base: "white" },
                inactiveThumb: { base: "white" },
                active: { base: "#add8e9", hover: "#add8e9" },
                inactive: { base: "#ccc", hover: "#ccc" },
              }}
              thumbAnimateRange={[0, 52]}
            />
          </div>

          <div className="ms-auto">
            <Dropdown>
              <Dropdown.Toggle
                as="span"
                className="material-symbols-outlined"
                style={{ cursor: "pointer" }}
                id="dropdown-basic"
              >
                more_vert
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={handleMarkAllAsRead}>
                  Mark all as Read
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        {notifications.length > 0 || searchQuery.trim().length > 0 ? (
          <div className="d-flex py-2 search_closer align-items-center searchinput">
            <SearchInput
              ref={searchInputRef}
              data={notifications}
              searchKeys={[]}
              onSearchResult={setNotifications}
              placeholder="Search"
              onSearchQueryChange={handleSearchQueryChange}
              onInputChange={handleSearchQueryChangeForlength}
            />
            <button
              onClick={handleReset}
              className="reset_btn mt-1"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span className="material-icons reset_ic0">restart_alt</span>
            </button>
          </div>
        ) : null}

        <Accordion
          activeKey={activeKey}
          onSelect={(key) => setActiveKey(key as string)}
          className="notificationaccord"
        >
          {notifications.map((notification, index) => {
            const isRead = notification.readStatus === "Read";
            const iconClass =
              Notification_Icons[
                notification.eventName as keyof typeof Notification_Icons
              ] || "notifications";

            return (
              <Accordion.Item
                key={index}
                eventKey={String(index)}
                className={`mb-2 ${
                  notification.isRead === 1 ? "read-notification" : ""
                }`}
                style={{
                  backgroundColor: isRead ? "#fafbfd" : "white",
                  opacity: isRead ? 0.8 : 1,
                }}
              >
                <Accordion.Header
                  onClick={() => {
                    const isActive = activeKey === String(index);
                    setActiveKey(isActive ? null : String(index));

                    if (notification.isRead !== 1) {
                      updateNotification(
                        String(notification.notificationRequestRecipientId),
                        false,
                        1, // 👈 mark as read
                      );
                    }
                  }}
                >
                  <div className="d-flex align-items-center w-100 notification-accord">
                    <div className="me-auto d-flex align-items-center">
                      <span className="material-icons material-symbols-outlined me-2 theme-color">
                        {iconClass}
                      </span>
                      <span>{notification.eventName}</span>
                    </div>
                    <div>
                      <span style={{ marginLeft: "auto", marginRight: "10px" }}>
                        {new Date(notification.createdDate)
                          .toLocaleString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                          .replace(",", " ")}
                      </span>
                    </div>
                    <div>
                      <span
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          display: "inline-block",
                          backgroundColor:
                            notification.isRead === 1
                              ? "transparent"
                              : getPriorityColor(notification.priority),
                          border: `2px solid ${getPriorityColor(
                            notification.priority,
                          )}`,
                          cursor:
                            notification.isRead === 1 ? "pointer" : "default", // clickable only if already read
                        }}
                        onClick={(e) => {
                          e.stopPropagation(); // prevent accordion toggle

                          if (notification.isRead === 1) {
                            updateNotification(
                              String(
                                notification.notificationRequestRecipientId,
                              ),
                              false,
                              0,
                            );
                            setActiveKey(null);
                          }
                        }}
                      ></span>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {[
                    Notification_Type.Appointment_Scheduling,
                    Notification_Type.Appointment_ReScheduling,
                  ].includes(notification.eventName) ? (
                    (() => {
                      const details = getAppointmentDetails(
                        notification.notificationMessage,
                      );
                      return (
                        <>
                          <div className="basic-message">
                            {details.baseMessage}
                          </div>
                          <div className="d-flex justify-content-start  me-1 mb-1">
                            <div className="mb-0 d-flex align-items-center w-30">
                              {" "}
                              On :{" "}
                            </div>
                            <div className="readnotedetail">
                              <p className="mb-0">
                                {/* {details.dateTime?.split(' | ')[0]} */}
                                {formatDateTimeWithRange(details.dateTime)}
                              </p>
                              <p className="mb-0">
                                {details.dateTime?.split(" | ")[1]}
                              </p>
                            </div>
                          </div>
                          <div className="d-flex justify-content-start  me-1 mb-2">
                            <div className="mb-0 d-flex align-items-start w-30">
                              At :{" "}
                            </div>
                            <div className="btn-text w-100 ">
                              {details.location}
                            </div>
                          </div>
                          <div className="daycount d-flex justify-content-between">
                            <div className="in-person">
                              {details.mode && <> {details.mode}</>}
                            </div>
                            <div className="mt-2">
                              {getElapsedTime(notification.createdDate)}
                            </div>
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <>
                      <p>{notification.notificationMessage}</p>
                      <p className="daycount">
                        {getElapsedTime(notification.createdDate)}
                      </p>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>

        {notifications?.length === 0 && (
          <div className="d-flex align-items-center justify-content-center flex-column mt-2 searchinput notification">
            {searchQuery.trim().length >= 3 ? (
              <p>No record found</p>
            ) : (
              <>
                <div className="text-center">
                  <img
                    src="/nobell.png"
                    alt="nobell"
                    className="w-40 shake-once"
                  />
                </div>
                <p>
                  You've{" "}
                  <span style={{ fontWeight: "600" }}>
                    {showRead ? "unread" : "read"}
                  </span>{" "}
                  all your notifications
                </p>
              </>
            )}
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

const getPriorityColor = (priority: string) => {
  if (priority === "Critical") return "#ed3239";
  if (priority === "Medium") return "#0098d7";
  return "#2dbb7a";
};

export default NotificationDrawer;
